import React from 'react';
import { useIntl } from 'react-intl';
import { TypedMemo } from '../../../common/types';
import { CatalogItem } from '../../../../redux/api/fymAPI.types';
import { useCatalogSong } from '../../../smartLinks/catalog/song/useCatalogSong';
import { formatSongLength } from '../../../smartLinks/catalog/song/masterFile';
import { catalogSongMessages } from '../../../smartLinks/catalog/song/catalogSongMessages';
import { MasterPlayButton } from '../../../smartLinks/catalog/song/player/MasterPlayButton';
import { MasterPlayerBar } from '../../../smartLinks/catalog/song/player/MasterPlayerBar';
import { MasterDownloadButton } from '../../../smartLinks/catalog/song/player/MasterDownloadButton';
import { masterPlayerMessages } from '../../../smartLinks/catalog/song/player/masterPlayerMessages';
import { useMasterPlayer } from '../../../smartLinks/catalog/song/player/useMasterPlayer';
import { catalogSongFieldMessages } from './catalogSongFieldMessages';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the song files; each part uses its own slice of it */
import styles from '../../../smartLinks/catalog/song/SongFiles.module.scss';
import playerStyles from '../../../smartLinks/catalog/song/player/MasterPlayer.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface CatalogSongReadyLineProps {
  accountId: number;
  item: CatalogItem;
}

/**
 * The song of a row with a master that did not fail (designs k2, release-kit o1): the play
 * button, the file name with the length and the hook, the bar, and Download. The lyric state is
 * its own row (`SongLyricsFact`). Replace is only on the song page in Catalog. While the AAC copy is not ready, the button is grey and the bar
 * says when the player is ready; Download works at once. While the analysis still runs, the
 * player works and the line says "Reading the song".
 */
const CatalogSongReadyLineComponent: React.FC<CatalogSongReadyLineProps> = ({ accountId, item }) => {
  const intl = useIntl();
  const { song } = useCatalogSong({ accountId, itemUuid: item.uuid });
  const player = useMasterPlayer({ accountId, itemUuid: item.uuid });

  const master = song?.master ?? null;
  const canPlay = master?.canPlay ?? item.song.canPlay ?? item.song.masterStatus === 'ready';
  const isReading = (master?.status ?? item.song.masterStatus) === 'processing';
  const durationSec = master?.durationSec ?? item.song.durationSec ?? player.durationSec;
  const hookStartSec = master?.hookStartSec ?? null;

  const facts = isReading
    ? [intl.formatMessage(catalogSongMessages.cellProcessing)]
    : [
        durationSec === null ? null : formatSongLength(durationSec),
        hookStartSec === null
          ? null
          : intl.formatMessage(catalogSongFieldMessages.hookAt, { time: formatSongLength(hookStartSec) }),
      ].filter((part): part is string => part !== null);
  const fileName = master?.name ?? intl.formatMessage(catalogSongFieldMessages.usesMaster);

  return (
    <div className={playerStyles.row}>
      <MasterPlayButton status={player.status} disabled={!canPlay} onToggle={player.toggle} />
      <div className={playerStyles.body}>
        <div className={playerStyles.titleLine}>
          <span className={styles.strong}>{fileName}</span>
          <span className={styles.mono}>{facts.join(' · ')}</span>
        </div>
        <MasterPlayerBar
          positionSec={player.positionSec}
          durationSec={player.durationSec ?? durationSec}
          hookStartSec={hookStartSec}
          disabled={!canPlay}
          note={canPlay ? undefined : intl.formatMessage(masterPlayerMessages.processing)}
          onSeek={player.seek}
        />
      </div>
      <MasterDownloadButton accountId={accountId} itemUuid={item.uuid} fileName={fileName} />
    </div>
  );
};

export const CatalogSongReadyLine = TypedMemo(CatalogSongReadyLineComponent);
